<template>
    <Panel
        v-if="profile"
        class="w-full md:h-full py-6 max-md:container md:px-6"
        :title="titleStatsPanel"
    >
        <div class="flex items-center justify-center md:h-full lg:flex lg:flex-col lg:items-center lg:justify-center">
            <div class="stats-container">
                <div class="stats-item">
                    <div class="flex flex-row items-center">
                        <i class="w-8 h-8 mr-2 text-purple-700 icon icon-badge-karma-full"></i>
                        <Badge
                            appearance="karma"
                            class="mr-2 text-center"
                        >
                            {{ profile.karma }}
                        </Badge>
                    </div>
                    <span>
                        {{ t('show.stats.label_karma') }}
                    </span>
                </div>
                <div
                    v-if="isOwner"
                    class="stats-item"
                >
                    <div class="flex flex-row items-center">
                        <i class="w-8 h-8 mr-2 text-orange-200 icon colored icon-badge-credit-full"></i>
                        <Badge
                            appearance="credits"
                            class="mr-2 text-center"
                        >
                            {{ profile.credits }}
                        </Badge>
                    </div>
                    <span class="flex flex-row items-center">
                        {{ t('show.stats.label_credits') }}
                        <BtnMenu
                            v-if="user && user.gamification.credits && user.gamification.credits.detailed"
                            ref="menuCredits"
                            class="ml-2 credits-menu"
                            menu-popover-size-class="max-w-xs"
                            :menu-popover-style="{
                                right: '-30px',
                                top: '30px',
                            }"
                            left-icon="info"
                            appearance="plain"
                            :menu-title="`${t('index.credits_menu_label')} ${user.gamification.credits.total}`"
                        >
                            <CreditsBreakdownList
                                :credits="user.gamification.credits"
                                :is-small-screen="screen.maxSM"
                            ></CreditsBreakdownList>
                        </BtnMenu>
                    </span>
                </div>
                <div
                    v-else
                    class="stats-item"
                >
                    <div class="flex flex-row items-center">
                        <i class="w-8 h-8 mr-2 text-purple-700 icon colored icon-badge-karma-animal"></i>
                        <Badge
                            appearance="karma"
                            class="mr-2 text-center"
                        >
                            {{ profile.karma_ranking }}
                        </Badge>
                    </div>
                    <span>
                        {{ t('show.stats.label_karma_status') }}
                    </span>
                </div>
                <div class="stats-item">
                    <div class="flex flex-row items-center">
                        <i class="w-8 h-8 mr-2 icon icon-downloads text-black"></i>
                        <Badge
                            appearance="default"
                            class="mr-2 text-center"
                        >
                            {{ countDownloads }}
                        </Badge>
                    </div>
                    <span>
                        {{ t('show.stats.label_downloads') }}
                    </span>
                </div>
            </div>
        </div>
        <template #header-options>
            <div>
                <Spinner v-if="progress"></Spinner>
                <i
                    v-if="error !== null"
                    v-tooltip="error"
                    class="w-6 h-6 text-red-600 icon icon-error"
                    @click="retrieveProfileStats"
                ></i>
            </div>
        </template>
    </Panel>
</template>

<script>
import Badge from '@components/Badge';
import Panel from '@components/Panel';
import CreditsBreakdownList from '@components/CreditsBreakdownList';
import { mapGetters } from 'vuex';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'ProfileShowStats',
    components: { Badge, Panel, CreditsBreakdownList },
    setup() {
        const { t } = useTranslation(['profile', 'reward']);
        return { t };
    },
    data() {
        return {
            progress: false,
            error: null,
        };
    },
    computed: {
        ...mapGetters({
            user: 'auth/user',
            profile: 'profile/profile',
            isProfileOwner: 'profile/isProfileOwner',
            screen: 'ui/screen',
        }),
        isOwner() {
            return this.isProfileOwner(this.user);
        },
        titleStatsPanel() {
            return this.isOwner ? this.t('show.stats.title_owner') : this.t('show.stats.title');
        },
        countDownloads() {
            return this.profile.stats ? this.profile.stats.downloads_generated : '-';
        },
    },
    mounted() {
        if (this.isOwner) {
            this.$store.dispatch('user/retrieveCredits');
        }
        this.$nextTick(() => this.retrieveProfileStats());
    },
    methods: {
        retrieveProfileStats() {
            this.error = null;
            this.progress = true;
            this.$store
                .dispatch('profile/retrieveStats', { id: this.profile.id })
                .catch((error) => {
                    this.error = error.response.data.message || error.response.data.error || 'An error occurred';
                })
                .finally(() => {
                    this.progress = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.stats-container {
    @apply flex flex-col items-center justify-start w-full;
    @screen lg {
        @apply flex-row items-start justify-between;
    }
    .stats-item {
        @apply flex flex-row items-center justify-start w-full mb-6 text-sm text-left text-center text-slate-400;
        &:last-child {
            @apply mb-0;
        }
        @screen lg {
            @apply flex-col justify-center w-1/3 mb-0;
        }
        span {
            @apply leading-normal;
            @screen md {
                @apply text-center;
            }
        }
    }
}
</style>
